// src/pages/administrators/SaleSubmissionsAdmin.tsx
import React, {useState, useEffect, CSSProperties } from 'react';
import {collection, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import {db } from '../../firebase';

interface SaleSubmission {
  id: string;
  itemName: string;
  description: string;
  category: string;
  proposedPrice: number;
  contactInfo: string;
  imageUrls: string[];
  status: 'Pending' | 'Approved' | 'Rejected';
  submittedAt: Date;
}

const styles: {[key: string]: CSSProperties } = {
  container: {
    padding: '2rem',
    fontFamily: 'Arial, sans-serif',
  },
  card: {
    backgroundColor: '#fff',
    padding: '2rem',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    marginBottom: '2rem',
  },
  heading: {
    fontSize: '1.5rem',
    marginBottom: '1rem',
  },
  searchBar: {
    width: '100%',
    padding: '0.5rem',
    marginBottom: '1rem',
    borderRadius: '4px',
    border: '1px solid #ccc',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '1rem',
  },
  th: {
    textAlign: 'left',
    padding: '0.75rem',
    borderBottom: '2px solid #ddd',
  },
  td: {
    padding: '0.75rem',
    borderBottom: '1px solid #eee',
    verticalAlign: 'top',
  },
  statusPending: {
    color: '#ffc107',
    fontWeight: 'bold',
  },
  statusApproved: {
    color: '#28a745',
    fontWeight: 'bold',
  },
  statusRejected: {
    color: '#dc3545',
    fontWeight: 'bold',
  },
  actionButton: {
    padding: '0.3rem 0.6rem',
    borderRadius: '4px',
    border: 'none',
    color: '#fff',
    cursor: 'pointer',
    marginRight: '0.5rem',
    marginBottom: '0.2rem',
  },
  approveButton: {
    backgroundColor: '#28a745',
  },
  rejectButton: {
    backgroundColor: '#dc3545',
  },
  deleteButton: {
    backgroundColor: '#6c757d',
  },
  viewButton: {
    backgroundColor: '#007bff',
  },
  imageGallery: {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap',
    marginTop: '0.5rem',
  },
  image: {
    width: '50px',
    height: '50px',
    objectFit: 'cover',
    borderRadius: '4px',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: '#fff',
    padding: '2rem',
    borderRadius: '8px',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
    maxWidth: '600px',
    width: '90%',
    position: 'relative',
    maxHeight: '80vh',
    overflowY: 'auto',
  },
  closeButton: {
    position: 'absolute',
    top: '1rem',
    right: '1rem',
    border: 'none',
    background: 'none',
    fontSize: '1.5rem',
    cursor: 'pointer',
  },
  modalDetail: {
    marginBottom: '1rem',
  },
  modalImageGallery: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
    gap: '1rem',
  },
  modalImage: {
    width: '100%',
    height: 'auto',
    borderRadius: '4px',
  },
};

const SaleSubmissionsAdmin = () => {
  const [submissions, setSubmissions] = useState<SaleSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSubmission, setSelectedSubmission] = useState<SaleSubmission | null>(null);

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'saleSubmissions'));
      const submissionsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        submittedAt: doc.data().submittedAt.toDate(),
      })) as SaleSubmission[];
      setSubmissions(submissionsData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching sale submissions:", error);
      setLoading(false);
    }
  };

  const updateSubmissionStatus = async (id: string, newStatus: 'Approved' | 'Rejected') => {
    try {
      const submissionRef = doc(db, 'saleSubmissions', id);
      await updateDoc(submissionRef, {status: newStatus });
      await fetchSubmissions();
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const deleteSubmission = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this sale submission?")) {
      try {
        await deleteDoc(doc(db, 'saleSubmissions', id));
        await fetchSubmissions();
      } catch (error) {
        console.error("Error deleting submission:", error);
      }
    }
  };

  const filteredSubmissions = submissions.filter(submission =>
    submission.itemName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    submission.contactInfo?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    submission.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h2 style={styles.heading}>User Sale Submissions</h2>
        <input
          type="text"
          placeholder="Search by item name, contact, or category"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={styles.searchBar}
        />
        {loading ? (
          <p>Loading submissions...</p>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Item Name</th>
                <th style={styles.th}>Category</th>
                <th style={styles.th}>Price</th>
                <th style={styles.th}>Contact</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSubmissions.length > 0 ? (
                filteredSubmissions.map((submission) => (
                  <tr key={submission.id}>
                    <td style={styles.td}>
                      <strong>{submission.itemName}</strong>
                      <br />
                      <small>{submission.description}</small>
                    </td>
                    <td style={styles.td}>{submission.category}</td>
                    <td style={styles.td}>UGX {submission.proposedPrice.toLocaleString()}</td>
                    <td style={styles.td}>{submission.contactInfo}</td>
                    <td style={styles.td}>
                      <span
                        style={
                          submission.status === 'Approved'
                            ? styles.statusApproved
                            : submission.status === 'Rejected'
                            ? styles.statusRejected
                            : styles.statusPending
                        }
                      >
                        {submission.status}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <button
                        style={{...styles.actionButton, ...styles.viewButton }}
                        onClick={() => setSelectedSubmission(submission)}
                      >
                        View
                      </button>
                      {submission.status === 'Pending' && (
                        <>
                          <button
                            style={{...styles.actionButton, ...styles.approveButton }}
                            onClick={() => updateSubmissionStatus(submission.id, 'Approved')}
                          >
                            Approve
                          </button>
                          <button
                            style={{...styles.actionButton, ...styles.rejectButton }}
                            onClick={() => updateSubmissionStatus(submission.id, 'Rejected')}
                          >
                            Reject
                          </button>
                        </>
                      )}
                      {(submission.status === 'Approved' || submission.status === 'Rejected') && (
                        <button
                          style={{...styles.actionButton, ...styles.deleteButton }}
                          onClick={() => deleteSubmission(submission.id)}
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} style={styles.td}>No sale submissions found.</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {selectedSubmission && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <button style={styles.closeButton} onClick={() => setSelectedSubmission(null)}>&times;</button>
            <h3 style={{ borderBottom: '2px solid #ccc', paddingBottom: '0.5rem' }}>Submission Details</h3>
            <div style={styles.modalDetail}>
              <strong>Item Name:</strong> {selectedSubmission.itemName}
            </div>
            <div style={styles.modalDetail}>
              <strong>Description:</strong> {selectedSubmission.description}
            </div>
            <div style={styles.modalDetail}>
              <strong>Category:</strong> {selectedSubmission.category}
            </div>
            <div style={styles.modalDetail}>
              <strong>Proposed Price:</strong> UGX {selectedSubmission.proposedPrice.toLocaleString()}
            </div>
            <div style={styles.modalDetail}>
              <strong>Contact Info:</strong> {selectedSubmission.contactInfo}
            </div>
            <div style={styles.modalDetail}>
              <strong>Status:</strong> {selectedSubmission.status}
            </div>
            <div style={styles.modalDetail}>
              <strong>Submitted At:</strong> {selectedSubmission.submittedAt.toLocaleString()}
            </div>
            <div style={styles.modalDetail}>
              <strong>Images:</strong>
              <div style={styles.modalImageGallery}>
                {selectedSubmission.imageUrls.map((url, index) => (
                  <a href={url} target="_blank" rel="noopener noreferrer" key={index}>
                    <img src={url} alt={`Item image ${index + 1}`} style={styles.modalImage} />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SaleSubmissionsAdmin;